import { useEffect, useState } from 'react'
import {
  subscribeToCategories,
  subscribeToMaterials,
  subscribeToUnits,
} from '@/features/materials/materials-api'
import type { Material, MaterialCategory, UnitOfMeasure } from '@/lib/types'

function permissionMessage(error: Error, collectionName: string): string {
  return error.message.includes('permission')
    ? `Firestore отклонил чтение коллекции ${collectionName}. Проверьте правила и роль admin.`
    : error.message
}

type MaterialsState = {
  materials: Material[]
  isLoading: boolean
  error: string | null
}

/** Live catalog visible to the signed-in admin (own items + published items). */
export function useMaterials(uid: string | undefined): MaterialsState {
  const [state, setState] = useState<MaterialsState>({
    materials: [],
    isLoading: true,
    error: null,
  })

  useEffect(() => {
    if (!uid) return
    setState({ materials: [], isLoading: true, error: null })
    return subscribeToMaterials(
      uid,
      (materials) => setState({ materials, isLoading: false, error: null }),
      (error) =>
        setState({
          materials: [],
          isLoading: false,
          error: permissionMessage(error, 'materials'),
        }),
    )
  }, [uid])

  return state
}

/** Categories and units: needed by the editor selects, cheap enough to keep live. */
export function useCategories(): {
  categories: MaterialCategory[]
  isLoading: boolean
  error: string | null
} {
  const [categories, setCategories] = useState<MaterialCategory[]>([])
  const [isLoading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(
    () =>
      subscribeToCategories(
        (items) => {
          setCategories(items)
          setLoading(false)
        },
        (caught) => {
          setLoading(false)
          setError(permissionMessage(caught, 'materialCategories'))
        },
      ),
    [],
  )

  return { categories, isLoading, error }
}

export function useUnits(): { units: UnitOfMeasure[]; isLoading: boolean; error: string | null } {
  const [units, setUnits] = useState<UnitOfMeasure[]>([])
  const [isLoading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(
    () =>
      subscribeToUnits(
        (items) => {
          setUnits(items)
          setLoading(false)
        },
        (caught) => {
          setLoading(false)
          setError(permissionMessage(caught, 'units'))
        },
      ),
    [],
  )

  return { units, isLoading, error }
}
